import type { CheckedChangedEventHandler } from '@sunwebgroup/ui-components'
import { useIntl } from '@/intl'
import { ShowMore } from '@/scopes/common/components/ShowMoreUDS'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { Accordion, IconGlyph, Typography } from '@sunwebgroup/ui-components/uds'
import { equals, isEmpty, reject } from 'ramda'
import { createSearchFilterClickEvent } from '../../../../domain/analytics'
import { useMultiSelectFilter } from '../../../../domain/hooks'
import ListFilterItem from './ListFilterItem'

const iconMap = new Map<string, IconGlyph>([
  // facilities
  ['LG', 'Bed'],
  ['BAR', 'Cocktail'],
  ['FITNESS', 'Dumbbell'],
  ['HAMMAM', 'Hamam'],
  ['INTERNET_PUBLIC_SPACE', 'WiFi'],
  ['JACUZZI', 'SwimmingPool'],
  ['PRIVATE_POOL', 'SwimmingPool'],
  ['SWIMMING_POOL', 'SwimmingPool'],
  ['SAFE', 'LockClosed'],
  ['LIFT', 'ElevatorFilledWithPeople'],
  ['MASSAGE', 'Towel'],
  ['ROOM_DISABLED_PEOPLE', 'PersonInWheelchair'],
  ['FACILITIES_DISABLED_PEOPLE', 'PersonInWheelchair'],
  ['PARKING_GARAGE', 'ParkingSign'],
  ['RECEPTION', 'PersonAtReception'],
  ['RESTAURANT', 'ForkAndKnive'],
  ['ROOM_SERVICE', 'ServingFoodWithCloche'],
  ['SAUNA', 'HouseHeating'],
  ['DUTCH_TV_CHANNELS', 'TvScreen'],
  ['PUBLIC_SPACE_TV', 'TvScreen'],
  ['LAUNDRY', 'WashingMachine'],
  ['SKI_STORAGE', 'Skis'],
  ['TENNIS_COURT', 'TennisBall'],

  // children facilities
  ['CHILDREN_CHAIR', 'ChildSeatHighChair'],
  ['CHILDREN_PLAYING_AREA', 'ToyHorseWithRoof'],
  ['CHILDREN_SWIMMING_POOL', 'BabyAndWaterWaves'],
  ['ENTERTAINMENT_FOR_CHILDREN', 'Clown'],
  ['SWIMMING_POOL_WITH_WATERSLIDE', 'WaterSlide'],

  // internet facilities
  ['FREE_WIFI', 'WiFi'],
  ['PAID_WIFI', 'WiFi'],
])

function getIconFromValue(value: string): IconGlyph | undefined {
  const icon = iconMap.get(value)
  return icon || 'Sliders'
}

export function FacilitiesFilter() {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { isFetching, options, selectedValues, setSelection } = useMultiSelectFilter('Facilities')
  const {
    isFetching: isFetchingChildren,
    options: childrenOptions,
    selectedValues: selectedChildrenValues,
    setSelection: setChildrenSelection,
  } = useMultiSelectFilter('ChildrenFacilities')
  const {
    isFetching: isFetchingInternet,
    options: internetOptions,
    selectedValues: selectedInternetValues,
    setSelection: setInternetSelection,
  } = useMultiSelectFilter('InternetFacilities')

  const handleFilterChange: CheckedChangedEventHandler = ({ checked, value }) => {
    if (checked) {
      setSelection([...selectedValues, value])
      sendEvent(
        createSearchFilterClickEvent({
          element: 'checkbox',
          type: 'general facility',
          result: [...selectedValues, value].join(','),
        }),
      )
    }

    if (!checked) {
      setSelection(reject(equals(value), selectedValues))
    }
  }

  const handleChildrenFilterChange: CheckedChangedEventHandler = ({ checked, value }) => {
    if (checked) {
      setChildrenSelection([...selectedChildrenValues, value])
      sendEvent(
        createSearchFilterClickEvent({
          element: 'checkbox',
          type: 'children facility',
          result: [...selectedChildrenValues, value].join(','),
        }),
      )
    }

    if (!checked) {
      setChildrenSelection(reject(equals(value), selectedChildrenValues))
    }
  }

  const handleInternetFilterChange: CheckedChangedEventHandler = ({ checked, value }) => {
    if (checked) {
      setInternetSelection([...selectedInternetValues, value])
      sendEvent(
        createSearchFilterClickEvent({
          element: 'checkbox',
          type: 'internet facility',
          result: [...selectedInternetValues, value].join(','),
        }),
      )
    }

    if (!checked) {
      setInternetSelection(reject(equals(value), selectedInternetValues))
    }
  }

  if (isEmpty(options) && isEmpty(childrenOptions) && isEmpty(internetOptions))
    return null

  return (
    <Accordion.Item
      title={intl.formatMessage({ id: 'facilities_label' })}
      defaultOpen={selectedValues.length > 0 || selectedChildrenValues.length > 0 || selectedInternetValues.length > 0}
    >
      {options.length > 0 && (
        <div className="mb-3">
          <Typography $variant="body-m-regular-snug" as="h3" className="my-2">
            {intl.formatMessage({ id: 'facilities_category_label_facilities' })}
          </Typography>
          <ShowMore
            openLabel={intl.formatMessage({ id: 'show_more' })}
            closeLabel={intl.formatMessage({ id: 'show_less' })}
          >
            {options.map(({ name, value, resultsCount }) => (
              <ListFilterItem.Checkbox
                key={name}
                value={value}
                checked={selectedValues.includes(value)}
                label={name}
                counter={resultsCount}
                glyph={getIconFromValue(value)}
                onChange={handleFilterChange}
                disabled={isFetching}
              />
            ))}
          </ShowMore>
        </div>
      )}

      {childrenOptions.length > 0 && (
        <div className="mb-3">
          <Typography $variant="body-m-regular-snug" as="h3" className="my-2">
            {intl.formatMessage({ id: 'facilities_category_label_children_facilities' })}
          </Typography>
          <ShowMore
            openLabel={intl.formatMessage({ id: 'show_more' })}
            closeLabel={intl.formatMessage({ id: 'show_less' })}
          >
            {childrenOptions.map(({ name, value, resultsCount }) => (
              <ListFilterItem.Checkbox
                key={name}
                value={value}
                checked={selectedChildrenValues.includes(value)}
                label={name}
                counter={resultsCount}
                glyph={getIconFromValue(value)}
                onChange={handleChildrenFilterChange}
                disabled={isFetchingChildren}
              />
            ))}
          </ShowMore>
        </div>
      )}

      {internetOptions.length > 0 && (
        <div className="mb-3">
          <Typography $variant="body-m-regular-snug" as="h3" className="my-2">
            {intl.formatMessage({ id: 'facilities_category_label_internet_facilities' })}
          </Typography>
          <ShowMore
            openLabel={intl.formatMessage({ id: 'show_more' })}
            closeLabel={intl.formatMessage({ id: 'show_less' })}
          >
            {internetOptions.map(({ name, value, resultsCount }) => (
              <ListFilterItem.Checkbox
                key={name}
                value={value}
                checked={selectedInternetValues.includes(value)}
                label={name}
                counter={resultsCount}
                glyph={getIconFromValue(value)}
                onChange={handleInternetFilterChange}
                disabled={isFetchingInternet}
              />
            ))}
          </ShowMore>
        </div>
      )}
    </Accordion.Item>
  )
}
